import React, { createContext, useContext, useRef } from "react"
import MyAlert from "@common-utils/MyAlert"
import MyCard from "@common-utils/MyCard"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyPagination from "@common-utils/MyPagination"
import { IAssurancesListingListContextPropsModel, IGetAssurancesResponseItemModel } from "./core/_models"
import { useAssurancesListingContext } from "./AssurancesListing"
import AssurancesListingListItem from "./AssurancesListingListItem"
import { CONST_DEFAULT_TOAST_OPTIONS } from "@common-constants/*"
import { toast } from "react-toastify"
import BulkSelectionContainerForListings from "../../../../utils/BulkSelectionContainerForListings"
import MyTooltip from "@common-utils/MyTooltip"

const AssurancesListingListContext = createContext<IAssurancesListingListContextPropsModel>({} as IAssurancesListingListContextPropsModel)
export const useAssurancesListingListContext = () => useContext(AssurancesListingListContext)

const AssurancesListingList = () => {
     const context = useAssurancesListingContext()
     const selectedItemRef = useRef<any>(null)

     // BULK SELECTION: handling the "check all elements" button click
     function handleCheckAllElementsInPageCallBack() {
          const items = context.listingQuery.data!.data
          context.setIsBulkSelectionEnabled(prev => ({
               ...prev,
               checkedElementsInPage: items,
          }))
          toast.info("Tous les éléments de cette page ont été cochés.", { ...CONST_DEFAULT_TOAST_OPTIONS, autoClose: 1000 })
     }

     // BULK SELECTION: handling the "check all elements across all pages" button click
     function handleCheckAllElementsAcrossAllPagesCallBack() {
          context.setIsBulkSelectionEnabled(prev => ({
               ...prev,
               areAllElementsInAllPagesChecked: true,
          }))
          toast.info("Tous les éléments correspondant à votre recherche ont été cochés.", { ...CONST_DEFAULT_TOAST_OPTIONS, autoClose: 1000 })
     }

     // BULK SELECTION: handling the "Quit" button click
     function handleQuitBulkSelectionModeCallBack() {
          context.setIsBulkSelectionEnabled({
               enabled: false,
               checkedElementsInPage: [],
               areAllElementsInAllPagesChecked: false,
          })
     }

     const CardHeaderRightSide = () => {
          return (
               <>
                    {context.listingQuery.isFetching ? (
                         <MySimpleSpinner size={"sm"} />
                    ) : (
                         <>
                              {context.listingQuery.data && context.listingQuery.data.totalElementsInPage > 0 && (
                                   <div className={"d-flex align-items-center"}>
                                        <MyPagination
                                             page={context.filters.page as number}
                                             pageStart={context.listingQuery.data.pageStart}
                                             pageEnd={context.listingQuery.data.pageEnd}
                                             totalPages={context.listingQuery.data.totalPages}
                                             totalElementsInAllPages={context.listingQuery.data.totalElementsInAllPages}
                                             onPreviousPageCallBack={() => {
                                                  context.setFilters(prev => ({ ...prev, page: (prev.page as number) - 1 }))
                                                  context.setItemInPreview(null)
                                             }}
                                             onNextPageCallBack={() => {
                                                  context.setFilters(prev => ({ ...prev, page: (prev.page as number) + 1 }))
                                                  context.setItemInPreview(null)
                                             }}
                                        />
                                   </div>
                              )}
                         </>
                    )}
               </>
          )
     }

     return (
          <AssurancesListingListContext.Provider value={{ selectedItemRef }}>
               <MyCard>
                    <MyCard.Header isCollapseIconShown={false} rightSideContent={<CardHeaderRightSide />}>
                         <div className="d-flex align-items-center">
                              <span className="symbol symbol-35px me-2">
                                   <span className="symbol-label bg-light-primary">
                                        <i className="fas fa-shield-alt text-primary fs-3"></i>
                                   </span>
                              </span>
                              <span className="fw-bolder fs-4">Liste des Assurances</span>
                         </div>
                    </MyCard.Header>

                    {/* Removed noPadding={true} to allow card spacing, handling overflow manually inside */}
                    <MyCard.Body>
                         <>
                              {/* Loading State */}
                              {context.listingQuery.isFetching && !context.listingQuery.data ? (
                                   <div className={"d-flex flex-column align-items-center justify-content-center p-10"}>
                                        <MySimpleSpinner size={"lg"} />
                                        <span className="text-gray-500 fs-6 fw-bold mt-2">Chargement des données...</span>
                                   </div>
                              ) : (
                                   <>
                                        {/* Error State */}
                                        {context.listingQuery.error ? (
                                             <MyAlert type={"danger"}>{context.listingQuery.error.response?.data.detail}</MyAlert>
                                        ) : (
                                             <>
                                                  {/* Empty State */}
                                                  {context.listingQuery.data && context.listingQuery.data.data.length === 0 && (
                                                       <div className="d-flex flex-column align-items-center justify-content-center py-10">
                                                            <div className="symbol symbol-75px mb-5">
                                                                 <span className="symbol-label bg-light-gray">
                                                                      <i className="fas fa-folder-open fs-1 text-gray-400"></i>
                                                                 </span>
                                                            </div>
                                                            <h5 className="text-gray-600">Aucun résultat trouvé</h5>
                                                            <span className="text-gray-400 fs-7">Essayez de modifier vos filtres</span>
                                                       </div>
                                                  )}

                                                  {/* Data List */}
                                                  {context.listingQuery.data && context.listingQuery.data.data.length > 0 && (
                                                       <>
                                                            {/* Bulk Selection Toolbar */}
                                                            {context.isBulkSelectionEnabled.enabled && (
                                                                 <div className="mb-4">
                                                                      <BulkSelectionContainerForListings
                                                                           areAllElementsInAllPagesChecked={context.isBulkSelectionEnabled.areAllElementsInAllPagesChecked}
                                                                           checkedElementIdsInPage={context.isBulkSelectionEnabled.checkedElementsInPage}
                                                                           totalElementsInPage={context.listingQuery.data.totalElementsInPage}
                                                                           totalElementsInAllPages={context.listingQuery.data.totalElementsInAllPages}
                                                                           allowCheckAllElementInAllPages={false}
                                                                           handleCheckAllElementsInPageCallBack={handleCheckAllElementsInPageCallBack}
                                                                           handleCheckAllElementsAcrossAllPagesCallBack={handleCheckAllElementsAcrossAllPagesCallBack}
                                                                           handleQuitBulkSelectionModeCallBack={handleQuitBulkSelectionModeCallBack}
                                                                           AllOrFewElementsCheckedInCurrentPageActionsChildren={
                                                                                <div className={"d-flex justify-content-center gap-2"}>
                                                                                     <MyTooltip title={"Marquer comme vérifié"} placement={"bottom"}>
                                                                                          <button
                                                                                               type={"button"}
                                                                                               className={`btn btn-sm btn-success fw-bold`}
                                                                                               onClick={() =>
                                                                                                    context.handleCheckAssurancesAsVerified(
                                                                                                         context.isBulkSelectionEnabled.checkedElementsInPage
                                                                                                    )
                                                                                               }
                                                                                          >
                                                                                               <i className="fas fa-check-circle fs-4 me-1" /> Valider la sélection
                                                                                          </button>
                                                                                     </MyTooltip>
                                                                                </div>
                                                                           }
                                                                      />
                                                                 </div>
                                                            )}

                                                            {/* List Container */}
                                                            <div
                                                                 style={{
                                                                      maxHeight: context.isBulkSelectionEnabled.enabled ? "59vh" : "68vh",
                                                                      overflowY: "auto",
                                                                      paddingRight: "5px", // Avoid scrollbar covering content
                                                                 }}
                                                            >
                                                                 {context.listingQuery.data.data.map((item: IGetAssurancesResponseItemModel, index) => (
                                                                      <AssurancesListingListItem key={item.mission.id} item={item} index={index} />
                                                                 ))}
                                                            </div>
                                                       </>
                                                  )}
                                             </>
                                        )}
                                   </>
                              )}
                         </>
                    </MyCard.Body>
               </MyCard>
          </AssurancesListingListContext.Provider>
     )
}

export default AssurancesListingList
